import upperFirst from 'lodash/upperFirst'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { GraphCompareResponse, GraphStackResponse } from 'src/api/gen'
import {
  formatExportTripsPerVehicle,
  formatExportTripsPerVehicleRatio,
  formatExportUtilizationRate,
  formatExportUtilizationRateComparing,
} from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import { Can, userHasPermission } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useProviderUser } from 'src/layout/hooks'

import { scrollToComponent } from '../hooks'

import {
  NumberOfTripsPerVehicle,
  RatioOfTripsPerVehiclePerProvider,
  UtilizationRate,
  UtilizationRatePerProvider,
} from './charts'

function UtilizationRateComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [comparing, setComparing] = useState<boolean>(false)
  const title = comparing
    ? upperFirst(t('kpi.UtilizationRate.title.comparing'))
    : upperFirst(t('kpi.UtilizationRate.title'))
  const description = comparing
    ? upperFirst(t('kpi.UtilizationRate.description.comparing'))
    : upperFirst(t('kpi.UtilizationRate.description'))

  async function fetcher() {
    const data = createPayloadFromFilters(props.filters)
    if (comparing) {
      return await api.graphs.graphUtilizationRatePerProvider({ data, options: props.apiOptions })
    } else {
      return await api.graphs.graphUtilizationRate({ data, options: props.apiOptions })
    }
  }

  function render(data: any, title?: string) {
    if (comparing) {
      return <UtilizationRatePerProvider title={title} graphReport={data} granularity={props.filters.granularity} />
    } else {
      return <UtilizationRate title={title} graphReport={data} granularity={props.filters.granularity} />
    }
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = comparing
      ? formatExportUtilizationRateComparing(props.filters.granularity)
      : formatExportUtilizationRate(props.filters.granularity)
  }

  return (
    <DashboardGraphWrapper
      id={props.id}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      toggles={
        userHasPermission(['read', 'kpi'])
          ? [
              {
                active: comparing,
                onToggle: () => setComparing(!comparing),
                activeIcon: 'bar',
                inactiveIcon: 'line',
                activeText: t('kpi.UtilizationCharts.toggleIconActiveText'),
                inactiveText: t('kpi.UtilizationCharts.toggleIconInactiveText'),
              },
            ]
          : []
      }
      updateOn={[comparing, props.filters]}
    />
  )
}

function TripsPerVehicleComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [showRatio, toggleRatio] = useState<boolean>(false)

  async function fetcher() {
    const data = createPayloadFromFilters(props.filters)
    if (showRatio) {
      return await api.graphs.graphRatioOfTripsPerVehiclePerProvider({ data, options: props.apiOptions })
    } else {
      return await api.graphs.graphNumberOfTripsPerVehicle({ data, options: props.apiOptions })
    }
  }

  function render(data: unknown, title?: string) {
    if (showRatio) {
      return (
        <RatioOfTripsPerVehiclePerProvider
          title={title}
          graphReport={data as GraphCompareResponse[]}
          granularity={props.filters.granularity}
        />
      )
    } else {
      return (
        <NumberOfTripsPerVehicle
          title={title}
          graphReport={data as GraphStackResponse[]}
          granularity={props.filters.granularity}
        />
      )
    }
  }

  const title = showRatio
    ? upperFirst(t('kpi.TripsPerVehicle.title.ratio'))
    : upperFirst(t('kpi.TripsPerVehicle.title'))
  const description = showRatio
    ? upperFirst(t('kpi.TripsPerVehicle.description.ratio'))
    : upperFirst(t('kpi.TripsPerVehicle.description'))

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = showRatio
      ? formatExportTripsPerVehicleRatio(props.filters.granularity)
      : formatExportTripsPerVehicle(props.filters.granularity)
  }

  return (
    <DashboardGraphWrapper
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      halfContainer={true}
      toggles={
        userHasPermission(['read', 'kpi'])
          ? [
              {
                active: showRatio,
                onToggle: () => toggleRatio(!showRatio),
                activeIcon: 'toggle-off',
                inactiveIcon: 'toggle-on',
                activeText: t('kpi.TripsPerVehicle.activeText'),
                inactiveText: t('kpi.TripsPerVehicle.inactiveText'),
              },
            ]
          : []
      }
      updateOn={[showRatio, props.filters]}
    />
  )
}

interface IUtilizationChartsProps {
  apiOptions?: Record<string, unknown>
  filters: IFilters
}

function UtilizationCharts(props: IUtilizationChartsProps) {
  const isProviderUser = useProviderUser()

  useEffect(scrollToComponent)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Can I="summarize" an="kpi">
        <>
          <TripsPerVehicleComponent filters={props.filters} apiOptions={props.apiOptions} />
          {isProviderUser ? null : (
            <UtilizationRateComponent id="utilization-rate" filters={props.filters} apiOptions={props.apiOptions} />
          )}
        </>
      </Can>
    </BaseChartWrapper>
  )
}

export default UtilizationCharts
